import React from 'react';

import MainLayout from 'layout/main-layout';
import PrivateRoute from 'layout/private-route';
import { RouteWithAuth } from 'layout/route-with-auth/RouteWithAuth';
import Login from 'pages/login';
import NotFoundPage from 'pages/not-found';
import { Navigate, Route, Routes } from 'react-router-dom';

const MePage = React.lazy(() => import('pages/me'));
const UserPage = React.lazy(() => import('pages/user'));
const TasksBoardMainPage = React.lazy(() => import('pages/tasks-board/main'));
const TasksBoardStatsPage = React.lazy(() => import('pages/tasks-board/stats'));
const TaskManagerPage = React.lazy(() => import('pages/task-manager'));
const TaskPage = React.lazy(() => import('pages/task'));
const TrackersPage = React.lazy(() => import('pages/trackers'));
const TeamPage = React.lazy(() => import('pages/team'));
const TeamCalendar = React.lazy(() => import('pages/team-calendar'));
const TeamsPage = React.lazy(() => import('pages/teams'));
const UsersPage = React.lazy(() => import('pages/users'));
const OfferPage = React.lazy(() => import('pages/affiliates/offers/offer'));
const OffersPage = React.lazy(() => import('pages/affiliates/offers/offers'));
const ManageAffiliatesPage = React.lazy(() => import('pages/affiliates/manage/ManageAffiliatesPage'));
const StoreShowcase = React.lazy(() => import('pages/store/showcase'));
const StoreOrder = React.lazy(() => import('pages/store/orders/order'));
const StoreOrders = React.lazy(() => import('pages/store/orders'));
const StoreProducts = React.lazy(() => import('pages/store/products'));
const PersonsPage = React.lazy(() => import('pages/affiliates/persons'));
const GalleryPage = React.lazy(() => import('pages/gallery'));
const LeadCalc = React.lazy(() => import('pages/affiliates/calculator'));
const DomainsManagePage = React.lazy(() => import('pages/domains/manage'));
const DomainsPage = React.lazy(() => import('pages/domains/my-domains'));
const DomainViasPage = React.lazy(() => import('pages/domains/vias'));
const CostsPage = React.lazy(() => import('pages/costs'));
const LeadsPage = React.lazy(() => import('pages/leads'));
const FBManagerPage = React.lazy(() => import('pages/fbmanager'));
const FBRulesPage = React.lazy(() => import('pages/fb-rules'));
const FBProxyPage = React.lazy(() => import('pages/fbmanager/proxy'));
const FBSettingsPage = React.lazy(() => import('pages/fbmanager/settings'));
const FBCommentsPage = React.lazy(() => import('pages/fbmanager/comments'));
const CandidatesPage = React.lazy(() => import('pages/candidates/candidates-table'));
const CandidatePage = React.lazy(() => import('pages/candidates/candidate-details'));
const VideoPage = React.lazy(() => import('pages/videos/video'));
const VideosPage = React.lazy(() => import('pages/videos/videos'));
const EventsPage = React.lazy(() => import('pages/capi/events'));
const FlowsPage = React.lazy(() => import('pages/capi/flows'));
const FlowPage = React.lazy(() => import('pages/capi/flow'));
const PixelsPage = React.lazy(() => import('pages/capi/pixels'));

export default function Router() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RouteWithAuth>
            <MainLayout />
          </RouteWithAuth>
        }
      >
        <Route index element={<Navigate to="/me" />} />
        <Route path="me" element={<PrivateRoute availableLevel="HR" />}>
          <Route index element={<MePage />} />
        </Route>
        <Route
          path="users"
          element={
            <PrivateRoute
              eligibleRoles={['PARTNER', 'HEAD', 'TEAM_LEAD', 'BIZDEV', 'MANAGER']}
              requiredPermissions={[{ BIZDEV: 'viewUsersPage', MANAGER: 'viewUsersPage' }]}
            />
          }
        >
          <Route index element={<UsersPage />} />
        </Route>
        <Route path="tasks" element={<PrivateRoute availableLevel="BUYER" />}>
          <Route index element={<TaskManagerPage />} />
          <Route path=":id" element={<TaskPage />} />
        </Route>
        <Route
          path="tasks-board"
          element={<PrivateRoute availableLevel="BIZDEV" requiredPermissions={['viewTasksBoardPage']} />}
        >
          <Route index element={<Navigate to="main" replace />} />

          <Route path="main" element={<TasksBoardMainPage />} />
          <Route
            path="stats"
            element={
              <PrivateRoute eligibleRoles={['BIZDEV']} requiredPermissions={['viewTasksBoardStatsPage']} />
            }
          >
            <Route index element={<TasksBoardStatsPage />} />
          </Route>
        </Route>
        <Route path="trackers" element={<PrivateRoute availableLevel="TEAM_LEAD" />}>
          <Route index element={<TrackersPage />} />
        </Route>
        <Route path="users/:id" element={<PrivateRoute availableLevel="TEAM_LEAD" />}>
          <Route index element={<UserPage />} />
        </Route>
        <Route path="teams" element={<PrivateRoute availableLevel="TEAM_LEAD" />}>
          <Route index element={<TeamsPage />} />
        </Route>
        <Route path="teams/:id" element={<PrivateRoute availableLevel="TEAM_LEAD" />}>
          <Route index element={<TeamPage />} />
        </Route>
        <Route path="calendar" element={<PrivateRoute availableLevel="HEAD" />}>
          <Route index element={<TeamCalendar />} />
        </Route>
        <Route path="calendar/:id" element={<PrivateRoute availableLevel="BUYER" />}>
          <Route index element={<TeamCalendar />} />
        </Route>
        <Route path="gallery" element={<PrivateRoute availableLevel="ADMIN" />}>
          <Route index element={<GalleryPage />} />
        </Route>
        <Route path="gallery/:id" element={<PrivateRoute availableLevel="BUYER" />}>
          <Route index element={<GalleryPage />} />
        </Route>
        <Route path="videos" element={<PrivateRoute requiredPermissions={['viewVideosPage']} />}>
          <Route index element={<VideosPage />} />
          <Route path="upload" element={<VideoPage />} />
          <Route path=":id" element={<VideoPage />} />
        </Route>
        <Route path="leads" element={<PrivateRoute availableLevel="MANAGER" />}>
          <Route index element={<LeadsPage />} />
        </Route>
        <Route
          path="domains"
          element={<PrivateRoute eligibleRoles={['PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER']} />}
        >
          <Route index element={<DomainsPage />} />
          <Route element={<PrivateRoute availableLevel="ADMIN" />}>
            <Route path="manage" element={<DomainsManagePage />} />
            <Route path="vias" element={<DomainViasPage />} />
          </Route>
        </Route>
        {/* Раздел управления доменами переехал в /domains — редиректы для старых ссылок */}
        <Route path="manage">
          <Route path="domains" element={<Navigate to="/domains/manage" replace />} />
          <Route path="vias" element={<Navigate to="/domains/vias" replace />} />
        </Route>
        <Route path="affiliates" element={<PrivateRoute availableLevel="MANAGER" />}>
          <Route path="offers" element={<PrivateRoute availableLevel="MANAGER" />}>
            <Route index element={<OffersPage />} />
          </Route>
          <Route path="offers/:id" element={<PrivateRoute availableLevel="MANAGER" />}>
            <Route index element={<OfferPage />} />
          </Route>
          <Route path="persons" element={<PersonsPage />} />
          <Route path="calculator" element={<LeadCalc />} />
          <Route
            path="manage"
            element={
              <PrivateRoute eligibleRoles={['BIZDEV']} requiredPermissions={['canManageAffiliates']} />
            }
          >
            <Route index element={<ManageAffiliatesPage />} />
          </Route>
        </Route>
        <Route path="store" element={<PrivateRoute availableLevel="BUYER" />}>
          <Route path="showcase" element={<StoreShowcase />} />
          <Route path="orders" element={<StoreOrders />} />
          <Route path="orders/:id" element={<StoreOrder />} />
          <Route path="products/:categoryId" element={<StoreProducts />} />
        </Route>
        <Route path="costs" element={<PrivateRoute availableLevel="MANAGER" />}>
          <Route index element={<CostsPage />} />
        </Route>
        <Route
          path="candidates"
          element={
            <PrivateRoute
              eligibleRoles={['PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER', 'HR']}
              requiredPermissions={[
                {
                  PARTNER: 'viewCandidatesPage',
                  HEAD: 'viewCandidatesPage',
                  TEAM_LEAD: 'viewCandidatesPage',
                  BUYER: 'viewCandidatesPage',
                  BIZDEV: 'viewCandidatesPage',
                  MANAGER: 'viewCandidatesPage',
                },
              ]}
            />
          }
        >
          <Route index element={<CandidatesPage />} />
          <Route path=":id" element={<CandidatePage />} />
        </Route>
        <Route
          path="capi"
          element={<PrivateRoute availableLevel="BUYER" requiredPermissions={['viewCapiPage']} />}
        >
          <Route path="flows">
            <Route index element={<FlowsPage />} />
            <Route path=":id" element={<FlowPage />} />
          </Route>
          <Route path="events" element={<EventsPage />} />
          <Route path="pixels" element={<PixelsPage />} />
        </Route>

        <Route path="fbmanager" element={<PrivateRoute availableLevel="FARMER" />}>
          <Route index path="" element={<Navigate to="main" replace />} />
          <Route index path="main" element={<FBManagerPage />} />
          <Route index path="main/accounts" element={<FBManagerPage />} />
          <Route index path="main/clients" element={<FBManagerPage />} />
          <Route index path="main/cabs" element={<FBManagerPage />} />
          <Route path="cabs">
            <Route index path="main" element={<FBManagerPage />} />
            <Route index path="rules" element={<FBRulesPage />} />
          </Route>
          <Route index path="main/campaigns" element={<FBManagerPage />} />
          <Route path="main/ads" element={<FBManagerPage />} />
          <Route index path="main/adsets" element={<FBManagerPage />} />
          <Route index path="proxy" element={<FBProxyPage />} />
          <Route index path="settings" element={<FBSettingsPage />} />
          <Route path="comments" element={<FBCommentsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
